import { CSSProperties } from 'react';

// How a template image sits relative to surrounding text. `left/center/right`
// put the image on its own line; `wrap-left/right` float it so following text
// flows around it.
//
// All of it travels as an inline `style` on the persisted `<img>` — the only
// channel the sanitizer leaves intact (`restoreImageRefs` keeps the style; it
// only strips `class`). So the backend `HtmlSanitizer` must allow the `float`,
// `display` and `margin` CSS properties for these to reach the preview/PDF/mail.
export type ImageAlign =
  | 'left'
  | 'center'
  | 'right'
  | 'wrap-left'
  | 'wrap-right';

// Single source of truth, shared by the NodeView (editor display) and
// `renderHTML` (persisted wire format).
export const IMAGE_ALIGN_STYLES: Record<ImageAlign, CSSProperties> = {
  left: { display: 'block', margin: '0.5rem auto 0.5rem 0' },
  center: { display: 'block', margin: '0.5rem auto' },
  right: { display: 'block', margin: '0.5rem 0 0.5rem auto' },
  'wrap-left': { float: 'left', margin: '0 1rem 0.5rem 0' },
  'wrap-right': { float: 'right', margin: '0 0 0.5rem 1rem' },
};

const toCssString = (style: CSSProperties): string =>
  Object.entries(style)
    .map(
      ([prop, value]) =>
        `${prop.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}: ${value}`,
    )
    .join('; ');

// Inline style string for the persisted `<img>` (used by `renderHTML`).
export const imageAlignToCss = (align: ImageAlign): string =>
  toCssString(IMAGE_ALIGN_STYLES[align]);

// Recover the alignment from a persisted `<img>`'s inline style on load, so the
// round-trip is stable (open → save → identical output).
export const parseImageAlign = (element: HTMLElement): ImageAlign | null => {
  const { cssFloat, marginLeft, marginRight } = element.style;
  if (cssFloat === 'left') return 'wrap-left';
  if (cssFloat === 'right') return 'wrap-right';
  if (marginLeft === 'auto' && marginRight === 'auto') return 'center';
  if (marginLeft === 'auto') return 'right';
  if (marginRight === 'auto') return 'left';
  return null;
};
